import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  clearCustomizationHistory,
  syncCustomizationHistoryWithCart,
} from "../Utils/customizationPreferenceHistory";

const CartContext = createContext(null);

const CART_STORAGE_KEY = "chaafo-cart-v1";

const EMPTY_CART = {
  items: [],
  restaurant: null,
  orderType: "takeaway",
};

const getNumber = (value) => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

// =========================================================
// CUSTOMIZATION NORMALIZATION
// Same customization ko hamesha same key mile
// =========================================================

const parseCustomization = (value) => {
  if (!value) {
    return {};
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);

      return parsed &&
        typeof parsed === "object" &&
        !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }

  if (
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    return value;
  }

  return {};
};

const sortCustomizationValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(sortCustomizationValue);
  }

  if (
    value &&
    typeof value === "object"
  ) {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        result[key] =
          sortCustomizationValue(value[key]);

        return result;
      }, {});
  }

  return value;
};

const stringifyCustomization = (value) => {
  try {
    const parsed = parseCustomization(value);

    const sorted =
      sortCustomizationValue(parsed);

    return JSON.stringify(sorted);
  } catch {
    return "{}";
  }
};

// =========================================================
// EXACT CART ROW MATCH
// itemId + size + restaurant + customization
// =========================================================

const getCartCustomizationKey = (item) => {
  return stringifyCustomization(
    item?.customization ??
      item?.selectedCustomizations ??
      {},
  );
};

const isSameCartRow = (cartItem, targetItem) => {
  if (!targetItem) {
    return false;
  }

  // =====================================================
  // OLD CALLS
  // Agar sirf itemId string bheji hai
  // =====================================================

  if (typeof targetItem !== "object") {
    return (
      String(cartItem?.itemId || "").trim() ===
      String(targetItem || "").trim()
    );
  }

  // =====================================================
  // EXACT ROW
  // =====================================================

  const cartItemId =
    String(cartItem?.itemId || "").trim();

  const targetItemId =
    String(
      targetItem?.itemId ||
        targetItem?.productId ||
        "",
    ).trim();

  const cartSize =
    String(
      cartItem?.sizeId ||
        cartItem?.size ||
        "Regular",
    ).trim();

  const targetSize =
    String(
      targetItem?.sizeId ||
        targetItem?.size ||
        "Regular",
    ).trim();

  const cartRestaurantId =
    String(
      cartItem?.restaurantId ||
        "",
    ).trim();

  const targetRestaurantId =
    String(
      targetItem?.restaurantId ||
        "",
    ).trim();

  const cartCustomizationKey =
    getCartCustomizationKey(cartItem);

  const targetCustomizationKey =
    getCartCustomizationKey(targetItem);

  return (
    cartItemId === targetItemId &&
    cartSize === targetSize &&
    cartRestaurantId === targetRestaurantId &&
    cartCustomizationKey ===
      targetCustomizationKey
  );
};

const getItemPriceDetails = (item) => {
  const firstPrice = Array.isArray(item?.prices) ? item.prices[0] || {} : {};

  const originalPrice = getNumber(
    firstPrice?.price || item?.price || item?.item_price,
  );

  const discountPrice = getNumber(
    firstPrice?.discount_price || item?.discount_price,
  );

  const discountPercentage = getNumber(firstPrice?.discount ?? item?.discount);

  const hasDiscount =
    discountPercentage > 0 &&
    discountPrice > 0 &&
    discountPrice < originalPrice;

  const unitPrice = hasDiscount ? discountPrice : originalPrice;

  const size = String(
    firstPrice?.size || firstPrice?.size_id || item?.size || "Regular",
  ).trim();

  return {
    firstPrice,
    unitPrice,
    originalPrice,
    size: size || "Regular",
  };
};

const createInitialCart = () => {
  if (typeof window === "undefined") {
    return EMPTY_CART;
  }

  try {
    const storedCart = window.sessionStorage.getItem(CART_STORAGE_KEY);

    if (!storedCart) {
      return EMPTY_CART;
    }

    const parsedCart = JSON.parse(storedCart);

    if (
      !parsedCart ||
      typeof parsedCart !== "object" ||
      !Array.isArray(parsedCart.items)
    ) {
      return EMPTY_CART;
    }

    return {
      items: parsedCart.items,

      restaurant: parsedCart.restaurant || null,

      orderType:
        String(parsedCart.orderType || "takeaway").trim() || "takeaway",
    };
  } catch (error) {
    console.error("Unable to restore cart:", error);

    return EMPTY_CART;
  }
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState(createInitialCart);

  // =========================================================
  // SAVE CART IN SESSION STORAGE
  // =========================================================

  useEffect(() => {
    try {
      // ============================================
      // CART EMPTY
      // Dono session keys completely delete
      // ============================================

      if (!Array.isArray(cart?.items) || cart.items.length === 0) {
        window.sessionStorage.removeItem(CART_STORAGE_KEY);

        clearCustomizationHistory();

        return;
      }

      // ============================================
      // SAVE CURRENT CART
      // ============================================

      window.sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));

      // ============================================
      // REMOVE CUSTOMIZATION HISTORY
      // OF ITEMS NO LONGER PRESENT IN CART
      // ============================================

      syncCustomizationHistoryWithCart(cart.items);
    } catch (error) {
      console.error("Unable to save cart:", error);
    }
  }, [cart]);

  const addToCart = useCallback(
    ({
      item,
      category,
      restaurant,
      orderType = "takeaway",
      selectedCustomizations = {},
    }) => {
      const itemId = String(
        item?.item_id || item?.itemId || item?.productId || "",
      ).trim();

      if (!itemId) {
        console.error("Item cannot be added because item ID is missing.", item);

        return;
      }

      const restaurantId = String(
        restaurant?.userid ||
          restaurant?.user_id ||
          restaurant?.shop_code ||
          "",
      ).trim();

      const { firstPrice, unitPrice, originalPrice, size } =
        getItemPriceDetails(item);

      const itemName = String(
        item?.item_name || item?.itemName || item?.productName || "Menu item",
      ).trim();

      const itemImage =
        item?.photo ||
        item?.itemImage ||
        item?.item_image ||
        category?.cat_img_url ||
        "";

      const customizationObject = sortCustomizationValue(
        parseCustomization(selectedCustomizations),
      );

      const customization = stringifyCustomization(customizationObject);

      const customizationKey = customization;

      const hasVarient = String(
        item?.hasVarient ||
          item?.hasVariant ||
          item?.has_varient ||
          item?.has_variant ||
          (Array.isArray(item?.prices) && item.prices.length > 0 ? "Y" : "N"),
      ).toUpperCase();

      setCart((previous) => {
        // Check if cart already contains items from another restaurant
        const cartRestaurantId = String(
          previous.restaurant?.userid ||
            previous.restaurant?.user_id ||
            previous.restaurant?.shop_code ||
            "",
        ).trim();

        let currentItems = previous.items;
        let currentRestaurant = previous.restaurant;

        // Different restaurant?
        if (previous.items.length > 0 && cartRestaurantId !== restaurantId) {
          currentItems = [];
          currentRestaurant = restaurant;
        }

        const existingItem = currentItems.find((cartItem) => {
          const existingCustomizationKey = stringifyCustomization(
            cartItem.customization ?? cartItem.selectedCustomizations ?? {},
          );

          return (
            String(cartItem.itemId) === itemId &&
            String(cartItem.sizeId) === size &&
            String(cartItem.restaurantId) === restaurantId &&
            existingCustomizationKey === customizationKey
          );
        });

        let updatedItems;

        if (existingItem) {
          updatedItems = currentItems.map((cartItem) => {
            const existingCustomizationKey = stringifyCustomization(
              cartItem.customization ?? cartItem.selectedCustomizations ?? {},
            );

            const isExactSameItem =
              String(cartItem.itemId) === itemId &&
              String(cartItem.sizeId) === size &&
              String(cartItem.restaurantId) === restaurantId &&
              existingCustomizationKey === customizationKey;

            if (!isExactSameItem) {
              return cartItem;
            }

            return {
              ...cartItem,
              quantity: Number(cartItem.quantity || 0) + 1,
            };
          });
        } else {
          updatedItems = [
            ...currentItems,
            {
              itemId,
              restaurantId, // <-- ADD THIS

              name: itemName,
              description: item?.item_description || "",
              image: itemImage,
              categoryName: category?.category_name || "",
              unitPrice,
              originalPrice,
              size,
              quantity: 1,

              productId: itemId,
              productName: itemName,
              actualName: itemName,
              itemImage,
              itemPrice: String(unitPrice),
              sizeId: size,
              sizeWords: size,
              customization,
              selectedCustomizations: customizationObject,
              hasVarient,
              selectedPrice: firstPrice,
            },
          ];
        }

        return {
          items: updatedItems,
          restaurant: restaurant,
          orderType: orderType || previous.orderType,
        };
      });
    },
    [],
  );

  // =========================================================
  // INCREASE EXACT CART ROW
  // =========================================================

  const increaseItem = useCallback((targetItem) => {
    setCart((previous) => {
      let targetIndex = -1;

      // Exact customized row
      if (targetItem && typeof targetItem === "object") {
        targetIndex = previous.items.findIndex((cartItem) =>
          isSameCartRow(cartItem, targetItem),
        );
      } else {
        // Old normal item call
        const cleanItemId = String(targetItem || "").trim();

        const matchingIndexes = previous.items
          .map((cartItem, index) => ({
            cartItem,
            index,
          }))
          .filter(
            ({ cartItem }) =>
              String(cartItem?.itemId || "").trim() === cleanItemId,
          );

        // IMPORTANT:
        // Sirf ek hi row hai tabhi ID based action allow
        if (matchingIndexes.length === 1) {
          targetIndex = matchingIndexes[0].index;
        }
      }

      if (targetIndex === -1) {
        return previous;
      }

      return {
        ...previous,

        items: previous.items.map((cartItem, index) => {
          if (index !== targetIndex) {
            return cartItem;
          }

          return {
            ...cartItem,
            quantity: Number(cartItem.quantity || 0) + 1,
          };
        }),
      };
    });
  }, []);

  // =========================================================
  // DECREASE EXACT CART ROW
  // =========================================================

  const decreaseItem = useCallback((targetItem) => {
    setCart((previous) => {
      let targetIndex = -1;

      // =====================================================
      // EXACT CUSTOMIZED ROW
      // =====================================================

      if (targetItem && typeof targetItem === "object") {
        targetIndex = previous.items.findIndex((cartItem) =>
          isSameCartRow(cartItem, targetItem),
        );
      } else {
        // =====================================================
        // NORMAL ITEM
        // Sirf ek matching row ho tabhi itemId use karo
        // Multiple customization ho to wrong last item
        // ko touch nahi karenge
        // =====================================================

        const cleanItemId = String(targetItem || "").trim();

        const matchingIndexes = previous.items
          .map((cartItem, index) => ({
            cartItem,
            index,
          }))
          .filter(
            ({ cartItem }) =>
              String(cartItem?.itemId || "").trim() === cleanItemId,
          );

        if (matchingIndexes.length === 1) {
          targetIndex = matchingIndexes[0].index;
        }
      }

      if (targetIndex === -1) {
        return previous;
      }

      const updatedItems = previous.items
        .map((cartItem, index) => {
          if (index !== targetIndex) {
            return cartItem;
          }

          return {
            ...cartItem,

            quantity: Math.max(0, Number(cartItem.quantity || 0) - 1),
          };
        })
        .filter((cartItem) => Number(cartItem.quantity || 0) > 0);

      return {
        ...previous,
        items: updatedItems,
      };
    });
  }, []);

  // =========================================================
  // REMOVE EXACT CART ROW
  // =========================================================

  const removeItem = useCallback((targetItem) => {
    setCart((previous) => {
      // =====================================================
      // CUSTOMIZED / EXACT ROW
      // =====================================================

      if (targetItem && typeof targetItem === "object") {
        const targetIndex = previous.items.findIndex((cartItem) =>
          isSameCartRow(cartItem, targetItem),
        );

        if (targetIndex === -1) {
          return previous;
        }

        return {
          ...previous,

          items: previous.items.filter((_, index) => index !== targetIndex),
        };
      }

      // =====================================================
      // NORMAL ITEM ID
      // Sirf ek matching row ho tab remove
      // =====================================================

      const cleanItemId = String(targetItem || "").trim();

      const matchingIndexes = previous.items
        .map((cartItem, index) => ({
          cartItem,
          index,
        }))
        .filter(
          ({ cartItem }) =>
            String(cartItem?.itemId || "").trim() === cleanItemId,
        );

      if (matchingIndexes.length !== 1) {
        return previous;
      }

      const targetIndex = matchingIndexes[0].index;

      return {
        ...previous,

        items: previous.items.filter((_, index) => index !== targetIndex),
      };
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart(EMPTY_CART);

    try {
      // Cart session
      window.sessionStorage.removeItem(CART_STORAGE_KEY);

      // Customization session
      clearCustomizationHistory();

      // Checkout cache
      window.localStorage.removeItem("chaafo-checkout-api-result");

      window.localStorage.removeItem("chaafo-checkout-signature");
    } catch (error) {
      console.error("Unable to clear cart storage:", error);
    }
  }, []);

  const setOrderType = useCallback((orderType) => {
    setCart((previous) => ({
      ...previous,
      orderType: String(orderType || "").trim() || previous.orderType,
    }));
  }, []);

  const getQuantity = useCallback(
    (itemId) => {
      const cleanItemId = String(itemId || "").trim();

      return cart.items
        .filter((item) => String(item.itemId || "").trim() === cleanItemId)
        .reduce((total, item) => total + Number(item.quantity || 0), 0);
    },
    [cart.items],
  );

  const itemCount = useMemo(() => {
    return cart.items.reduce(
      (total, item) => total + Number(item.quantity || 0),
      0,
    );
  }, [cart.items]);

  const subtotal = useMemo(() => {
    return cart.items.reduce(
      (total, item) =>
        total + Number(item.unitPrice || 0) * Number(item.quantity || 0),
      0,
    );
  }, [cart.items]);

  const value = useMemo(
    () => ({
      items: cart.items,
      restaurant: cart.restaurant,
      orderType: cart.orderType,

      itemCount,
      subtotal,

      addToCart,
      increaseItem,
      decreaseItem,
      removeItem,
      clearCart,
      getQuantity,
      setOrderType,
    }),
    [
      cart,
      itemCount,
      subtotal,
      addToCart,
      increaseItem,
      decreaseItem,
      removeItem,
      clearCart,
      getQuantity,
      setOrderType,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used inside CartProvider.");
  }

  return context;
}
